import { CalendarType, SupportedTerraProvidersType, SupportedTerraSDKType } from '@demind-inc/core';
import { FC, SVGProps, createElement } from 'react';
import AppleCalendarIcon from '../../assets/calendar/apple.png';
import { ReactComponent as GoogleIcon } from '../../assets/icons/google-icon.svg';
import { ReactComponent as OutlookIcon } from '../../assets/icons/outlook-icon.svg';

import { ReactComponent as HealthIcon } from '../../assets/settings/wearable/health-icon.svg';
import { ReactComponent as GarminIcon } from '../../assets/settings/wearable/garmin-icon.svg';
import { ReactComponent as WhoopIcon } from '../../assets/settings/wearable/whoop-icon.svg';
import { ReactComponent as FitbitIcon } from '../../assets/settings/wearable/fitbit-icon.svg';
import { ReactComponent as UltrahumanIcon } from '../../assets/settings/wearable/ultrahuman.svg';
import { ReactComponent as GoogleFitIcon } from '../../assets/settings/wearable/google-fit.svg';
import { ReactComponent as SamsungHealthIcon } from '../../assets/settings/wearable/samsung-icon.svg';
import { ReactComponent as OuraIcon } from '../../assets/settings/wearable/oura-icon.svg';
import { ReactComponent as MyFitnessPalIcon } from '../../assets/settings/wearable/myfitnesspal.svg';
import { ReactComponent as StravaIcon } from '../../assets/settings/wearable/strava.svg';

import {
  AIAssistantEntireDaySettings,
  AIAssistantMealSettings,
  AIAssistantWorkoutSettings,
  AIAssistantSleepSettings,
  AIAssistantMemorySettings,
  AIAssistantReplanSettings,
} from './SettingsModal';
import { LocationSettingsSection } from '../SettingsPageContent/ParentSections/GeneralSection';
import { isDesktopApp } from '../../utils/environment';

export interface WearbaleItem {
  id: SupportedTerraProvidersType | SupportedTerraSDKType;
  name: string;
  Icon: FC<SVGProps<SVGSVGElement>>;
}

export interface CalendarOptionsType {
  calendar: string;
  id: CalendarType;
  icon: React.ComponentType;
}

const AppleProviderIcon: FC<{ className?: string }> = ({ className }) =>
  createElement('img', {
    src: AppleCalendarIcon,
    alt: 'Apple Calendar',
    className,
  });

export const SIDEBAR_ICONS = {
  mobile: require('../../assets/icons/mobile_icon.png'),
  chrome: require('../../assets/icons/chrome_icon.png'),
  close: require('../../assets/icons/close_icon.png'),
  help: require('../../assets/icons/help-icon.svg'),
  feedback: require('../../assets/icons/feedback-icon.svg'),
  todoist: require('../../assets/todoIntegrations/todoist_icon.png'),
  trello: require('../../assets/todoIntegrations/trello_icon.png'),
  ticktick: require('../../assets/todoIntegrations/ticktick_icon.jpg'),
  clickup: require('../../assets/todoIntegrations/clickup.png'),
  github: require('../../assets/todoIntegrations/github.png'),
  'google-tasks': require('../../assets/todoIntegrations/google-tasks.png'),
  lifestack: require('../../assets/logo/logo_transparent.png'),
  'apple-reminders': require('../../assets/todoIntegrations/apple_reminders.png'),
  notion: require('../../assets/todoIntegrations/notion_icon.png'),
};

export const WEARABLES: WearbaleItem[] = [
  { id: 'APPLE', name: 'Health', Icon: HealthIcon },
  { id: 'GOOGLE_FIT', name: 'Google Fit', Icon: GoogleFitIcon },
  { id: 'SAMSUNG_HEALTH', name: 'Samsung Health', Icon: SamsungHealthIcon },
  { id: 'GARMIN', name: 'Garmin Connect™', Icon: GarminIcon },
  { id: 'WHOOP', name: 'Whoop', Icon: WhoopIcon },
  { id: 'FITBIT', name: 'Fitbit', Icon: FitbitIcon },
  { id: 'OURA', name: 'Oura', Icon: OuraIcon },
  { id: 'ULTRAHUMAN', name: 'Ultrahuman', Icon: UltrahumanIcon },
  { id: 'MYFITNESSPAL', name: 'MyFitnessPal', Icon: MyFitnessPalIcon },
  { id: 'STRAVA', name: 'Strava', Icon: StravaIcon },
];

export const MobileSdkOnlyWearbales: SupportedTerraSDKType[] = [
  'APPLE',
  'GOOGLE_FIT',
  'SAMSUNG_HEALTH',
];

export const calendars: CalendarOptionsType[] = [
  { calendar: 'Google', id: 'google', icon: GoogleIcon },
  { calendar: 'Outlook', id: 'outlook', icon: OutlookIcon },
  { calendar: 'Apple', id: 'apple', icon: AppleProviderIcon },
];

export const suggestionsItem: Array<{
  title: string;
  titleKey: string;
  component: React.ComponentType;
  disabled?: boolean;
}> = [
  {
    title: 'Plan-my-day',
    titleKey: 'settings.aiAssistant.items.autoUpdateSettings',
    component: AIAssistantReplanSettings,
  },
  {
    title: '📆 Work',
    titleKey: 'settings.aiAssistant.items.work',
    component: AIAssistantEntireDaySettings,
  },
  {
    title: '🍽️ Meal',
    titleKey: 'settings.aiAssistant.items.meal',
    component: AIAssistantMealSettings,
  },
  {
    title: '💪 Workout',
    titleKey: 'settings.aiAssistant.items.workout',
    component: AIAssistantWorkoutSettings,
  },
  {
    title: '😴 Sleep Suggestions', //TODO: need more settings to be added
    titleKey: 'settings.aiAssistant.items.sleepSuggestions',
    component: AIAssistantSleepSettings,
    disabled: true,
  },
];

export const personalizationItem: Array<{
  title: string;
  titleKey: string;
  component: React.ComponentType;
  disabled?: boolean;
}> = [
  {
    title: '🧠 Memory',
    titleKey: 'settings.aiAssistant.items.memory',
    component: AIAssistantMemorySettings,
  },
  ...(isDesktopApp()
    ? []
    : [
        {
          title: '📍 Location',
          titleKey: 'settings.aiAssistant.items.location',
          component: LocationSettingsSection,
        },
      ]),
];

export const aiAssistantSettingsSections = [
  { title: 'Suggestions', titleKey: 'settings.aiAssistant.suggestions', items: suggestionsItem },
  {
    title: 'Personalization',
    titleKey: 'settings.aiAssistant.personalization',
    items: personalizationItem,
  },
];
